/**
 * デバイス識別サービス
 * UUID v4でデバイスを一意に識別し、デバイスタイプを判定する
 */

import { v4 as uuidv4 } from 'uuid';

const DEVICE_ID_KEY = 'rcal_device_id';
const DEVICE_NAME_KEY = 'rcal_device_name';
const DEVICE_NUMBER_KEY = 'rcal_device_number';

type DeviceType = 'desktop' | 'tablet' | 'mobile';

interface DeviceInfo {
  deviceId: string;
  deviceName: string | null;
  deviceNumber: number | null;
  deviceType: DeviceType;
  userAgent: string;
  platform: string;
  language: string;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  touchSupport: boolean;
  pixelRatio: number;
}

/**
 * デバイスIDを取得（なければUUID v4で生成）
 */
export function getDeviceId(): string {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);

    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }

    return deviceId;
  } catch (error) {
    // LocalStorageが使えない場合（プライベートモード等）は一時的なIDを返す
    console.warn('LocalStorage not available, using temporary device ID');
    return `temp_${uuidv4()}`;
  }
}

/**
 * デバイス名を取得（初回はバックエンドから割り当て）
 */
export function getDeviceName(): string | null {
  try {
    return localStorage.getItem(DEVICE_NAME_KEY) || null;
  } catch (error) {
    return null;
  }
}

/**
 * デバイス名を設定（バックエンドから割り当てられた名前を保存）
 */
export function setDeviceName(name: string): void {
  try {
    localStorage.setItem(DEVICE_NAME_KEY, name);
  } catch (error) {
    console.warn('Failed to save device name:', error);
  }
}

/**
 * デバイス番号を取得
 */
export function getDeviceNumber(): number | null {
  try {
    const num = localStorage.getItem(DEVICE_NUMBER_KEY);
    return num ? parseInt(num, 10) : null;
  } catch (error) {
    return null;
  }
}

/**
 * デバイス番号を設定
 */
export function setDeviceNumber(number: number): void {
  try {
    localStorage.setItem(DEVICE_NUMBER_KEY, String(number));
  } catch (error) {
    console.warn('Failed to save device number:', error);
  }
}

/**
 * デバイスタイプを判定
 */
export function getDeviceType(): DeviceType {
  const ua = navigator.userAgent;

  // タブレット判定（iPad、Androidタブレット）
  if (/iPad|Android(?!.*Mobile)/i.test(ua)) {
    return 'tablet';
  }

  // モバイル判定（iPhone、Androidスマホ等）
  if (/iPhone|Android.*Mobile|webOS|iPod|BlackBerry|Windows Phone/i.test(ua)) {
    return 'mobile';
  }

  return 'desktop';
}

/**
 * デバイスのアイコン種別を取得（UI表示用）
 */
export function getDeviceIcon(): string {
  const type = getDeviceType();
  switch (type) {
    case 'mobile': return '📱';
    case 'tablet': return '📲';
    default: return '💻';
  }
}

/**
 * デバイスタイプからアイコンを取得（UI表示用）
 */
export function getDeviceIconByType(type: string): string {
  switch (type) {
    case 'mobile': return '📱';
    case 'tablet': return '📲';
    default: return '💻';
  }
}

/**
 * デバイス情報をまとめて取得
 */
export function getDeviceInfo(): DeviceInfo {
  const deviceType = getDeviceType();

  return {
    deviceId: getDeviceId(),
    deviceName: getDeviceName(),
    deviceNumber: getDeviceNumber(),
    deviceType: deviceType,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenWidth: screen.width,
    screenHeight: screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    touchSupport: 'ontouchstart' in window,
    pixelRatio: window.devicePixelRatio || 1,
  };
}

/**
 * プライベートブラウジングモードかどうかを検出
 */
export function isPrivateMode(): boolean {
  try {
    const testKey = '__rcal_private_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return false;
  } catch (e) {
    return true;
  }
}

/**
 * デバイス情報をリセット（テスト用）
 */
export function resetDeviceInfo(): void {
  try {
    localStorage.removeItem(DEVICE_ID_KEY);
    localStorage.removeItem(DEVICE_NAME_KEY);
    localStorage.removeItem(DEVICE_NUMBER_KEY);
  } catch (error) {
    console.warn('Failed to reset device info:', error);
  }
}
